import React from 'react';
import { useAppConfig, IMPRESSION_GOAL_MAX_DEFAULT } from '@/modules/config';
import { TopicLogic } from '../logic';
import type { Topic } from '../types';
import { fieldLabelStyle } from './styles';
import styles from './ImpressionGoalProgress.module.css';

interface ImpressionGoalProgressProps {
  topic: Topic;
}

/**
 * The topic's impression goal after clamping, or null when it has none — i.e.
 * whether {@link ImpressionGoalProgress} will render anything. Exported so the
 * engagement card can decide its separators without duplicating the clamp.
 */
export const useEffectiveImpressionGoal = (topic?: Topic): number | null => {
  const { features } = useAppConfig();
  const max =
    typeof features.impressionGoalMax === 'number'
      ? features.impressionGoalMax
      : IMPRESSION_GOAL_MAX_DEFAULT;
  if (!topic) return null;
  return TopicLogic.effectiveImpressionGoal(topic.impressionGoal, max);
};

/**
 * Progress toward the topic's impression goal (#447), shown on every topic
 * type's read view as a block of the engagement card. Renders nothing when the
 * topic has no goal.
 *
 * Both numbers come from the topic detail response, so this costs no extra
 * fetch: `impressionGoal` is the manager's target and `introCount` is the
 * topic's running impression total (it counts intros and news sends alike —
 * see issue #552 on renaming it).
 *
 * Reaching the goal is a milestone, not a limit: nothing here blocks sharing.
 */
const ImpressionGoalProgress: React.FC<ImpressionGoalProgressProps> = ({
  topic,
}) => {
  const { features } = useAppConfig();
  const max =
    typeof features.impressionGoalMax === 'number'
      ? features.impressionGoalMax
      : IMPRESSION_GOAL_MAX_DEFAULT;

  const goal = useEffectiveImpressionGoal(topic);
  if (goal == null) return null;

  const reached = TopicLogic.impressionGoalReached(
    topic.introCount,
    topic.impressionGoal,
    max
  );
  const count = topic.introCount ?? 0;
  // A zero goal is a valid emergency ceiling, and would divide by zero here.
  const pct = goal === 0 ? 100 : Math.min(100, (count / goal) * 100);

  return (
    <div
      className={`${styles.section} ${reached ? styles.reached : ''}`}
      data-testid={
        reached ? 'impression-goal-reached' : 'impression-goal-progress'
      }
    >
      {/* The shared field-label token, so this block's label matches the other
          blocks of the engagement card rather than restating their type. */}
      <p style={fieldLabelStyle}>Impression goal</p>
      <p className={styles.count}>
        {reached
          ? `Goal reached — ${count} ${count === 1 ? 'person' : 'people'}! 🎉`
          : `${count} of ${goal} ${count === 1 ? 'person' : 'people'} reached so far`}
      </p>
      <div className={styles.track}>
        <div
          className={styles.fill}
          style={{ '--progress': `${pct}%` } as React.CSSProperties}
        />
      </div>
      {!reached && (
        <p className={styles.note}>
          {goal - count} to go. Sharing keeps it moving.
        </p>
      )}
    </div>
  );
};

export default ImpressionGoalProgress;
